import React, { useState, useRef, useEffect } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  getBasicInfoQuestions,
  getWorksheetSectionBQuestions,
  getApplicableAllowancesQuestion,
  worksheetSectionAQuestions,
  stateOfDomicileQuestions,
  exemptionQuestions,
  additionalWithholdingAmtQuestion,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function DCForm({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  version,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  const basicInfoForm = useRef();
  const basicInfoQuestions = getBasicInfoQuestions(version);
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  const [worksheetOpen, setWorksheetOpen] = useState(false);

  const worksheetAForm = useRef();
  const worksheetAInitQ = getInitialQuestions(worksheetSectionAQuestions);
  const [worksheetA, setWorksheetA] = useState(worksheetAInitQ);

  const applicableAllowancesQuestion = getApplicableAllowancesQuestion(version);
  const worksheetApplicableAllowancesInitQ = getInitialQuestions(applicableAllowancesQuestion);
  const [applicableAllowances, setApplicableAllowances] = useState(worksheetApplicableAllowancesInitQ);

  const worksheetBForm = useRef();
  const worksheetSectionBQuestions = getWorksheetSectionBQuestions(version);
  const worksheetBInitQ = getInitialQuestions(worksheetSectionBQuestions);
  const [worksheetB, setWorksheetB] = useState(worksheetBInitQ);

  const worksheetForm = useRef();
  const additionalWithholdingAmtInitQ = getInitialQuestions(additionalWithholdingAmtQuestion);
  const [additionalWithholdingAmt, setAdditionalWithholdingAmt] = useState(additionalWithholdingAmtInitQ);

  const exemptionsForm = useRef();
  const [domicileNotDC, setDomicileNotDC] = useState(data.domicile_not_dc || false);

  const stateOfDomicileInitQ = getInitialQuestions(stateOfDomicileQuestions);
  const [stateOfDomicile, setStateOfDomicile] = useState(stateOfDomicileInitQ);

  const exemptionInitQ = getInitialQuestions(exemptionQuestions);
  const [exemptions, setExemptions] = useState(exemptionInitQ);

  useEffect(() => {
    setBasicInfo(basicInfoInitQ);
    setApplicableAllowances(worksheetApplicableAllowancesInitQ);
    setWorksheetB(worksheetBInitQ);
  }, [version]);

  const renderBasicInfoForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const [secALineI, setSecALineI] = useState(data.sec_a_line_i || 0);

  const calculateSectionAWithholding = () => {
    if (!worksheetOpen) return 0;

    const numBoxesChecked = applicableAllowances[0].answer.answer.applicable_allowances.length || 0;

    return numBoxesChecked + (parseInt(worksheetA[0].answer.answer.dependents, 10) || 0);
  };

  const renderWorksheetA = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={applicableAllowances}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = applicableAllowances.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setApplicableAllowances(questions);
        }}
      />
      <FormConsumer
        questions={worksheetA}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = worksheetA.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWorksheetA(questions);
        }}
        ref={worksheetAForm}
      />
      <S.QuestionLabel mb="7px">Total allowances</S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={worksheetOpen ? calculateSectionAWithholding() : secALineI}
            onChange={(e) => setSecALineI(e.target.value)}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
    </Flex>
  );

  const [secBLineN, setSecBLineN] = useState(data.sec_b_line_n || 0);

  const calculateSectionBWithholding = () => {
    if (!worksheetOpen) return 0;

    // Calculation for Version 12_13 //
    if (version === '12_13') {
      const lineJ = worksheetB[0].answer.answer.itemized_deductions || 0;

      let lineK;
      worksheetB[1].answer.answer.filing_status_b === 'other' ? (lineK = 4000) : (lineK = 2000);

      const lineL = parseInt(lineJ, 10) - parseInt(lineK, 10);
      const lineM = 1675 * calculateSectionAWithholding();
      const lineN = lineM !== 0 ? Math.round(lineL / lineM) : 0;

      return lineN >= 0 ? lineN : 0;
    }

    // Calculation for Version 10_17 //
    const lineJ = worksheetB[0].answer.answer.itemized_deductions || 0;

    let lineK;
    switch (worksheetB[1].answer.answer.filing_status_b) {
      case 'single_or_married_filing_separately_or_dependent':
        lineK = 6500;
        break;
      case 'head_of_household':
        lineK = 9550;
        break;
      case 'married_filing_separately_or_married_filing_jointly':
        lineK = 13000;
        break;
      default:
        lineK = 0;
        break;
    }

    const lineL = parseInt(lineJ, 10) - parseInt(lineK, 10);
    const lineM = Math.round(lineL / 4150.0);

    return lineM > 0 ? lineM : 0;
  };

  const renderWorksheetB = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={worksheetB}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = worksheetB.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setWorksheetB(questions);
        }}
        ref={worksheetBForm}
      />
      { /* Hide this section for Version 10_17 onwards */
        version === '12_13' && (
          <>
            <S.QuestionLabel mb="7px">Total additional allowances</S.QuestionLabel>
            <S.QuestionWrapper>
              <Flex mb="10px">
                <S.Input
                  type="text"
                  value={worksheetOpen ? calculateSectionBWithholding() : secBLineN}
                  onChange={(e) => setSecBLineN(e.target.value)}
                  disabled
                />
              </Flex>
            </S.QuestionWrapper>
          </>
        )
      }
    </Flex>
  );

  const [totalNumAllowances, setTotalNumAllowances] = useState(data.total_num_allowances || 0);
  const calculateTotalNumAllowances = () => {
    const res = calculateSectionAWithholding() + calculateSectionBWithholding();
    return res;
  };
  const renderWorksheet = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px">Total number of withholding allowances you're claiming</S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={worksheetOpen ? calculateTotalNumAllowances() : 0}
            onChange={(e) => setTotalNumAllowances(e.target.value)}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <CollapsibleQuestions
        title="If you need to calculate your allowances, use the worksheet for regular withholding"
        handleClick={() => setWorksheetOpen(!worksheetOpen)}
        open={worksheetOpen}
      >
        <Text style={{ fontSize: '15px', fontWeight: 600 }} mb="12px">
          Section A - Number of withholding allowances
        </Text>
        <Flex column>{renderWorksheetA(isReviewStep)}</Flex>
        <Text style={{ fontSize: '15px', fontWeight: 600 }} mb="12px" mt="10px">
          Section B - Additional withholding allowances
        </Text>
        <Flex column>{renderWorksheetB(isReviewStep)}</Flex>
      </CollapsibleQuestions>
      <FormConsumer
        questions={additionalWithholdingAmt}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = additionalWithholdingAmt.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAdditionalWithholdingAmt(questions);
        }}
        ref={worksheetForm}
      />
    </Flex>
  );

  const renderStateOptions = (isReviewStep) => (
    <FormConsumer
      questions={stateOfDomicile}
      {...(isReviewStep && {
        viewResultAsManager: true,
        reviewActions: [],
      })}
      onQuestionChange={({ question, value }) => {
        const questions = stateOfDomicile.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setStateOfDomicile(questions);
      }}
      ref={exemptionsForm}
    />
  );

  const renderExemptions = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px" isRequired>
        My domicile is a state other than the District of Columbia.
      </S.QuestionLabel>
      <S.QuestionWrapper mb="20px">
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={domicileNotDC}
              checked={domicileNotDC}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setDomicileNotDC(!domicileNotDC);
              }}
            />
            <Box ml={2} />
            Yes
          </Flex>
        </label>
        <label>
          <Flex alignItems="center">
            <S.Input
              type="radio"
              value={!domicileNotDC}
              checked={!domicileNotDC}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setDomicileNotDC(!domicileNotDC);
              }}
            />
            <Box ml={2} />
            No
          </Flex>
        </label>
      </S.QuestionWrapper>
      {domicileNotDC ? renderStateOptions(isReviewStep) : ''}
      <Flex column>
        <FormConsumer
          questions={exemptions}
          {...(isReviewStep && {
            viewResultAsManager: true,
            reviewActions: [],
          })}
          onQuestionChange={({ question, value }) => {
            const questions = exemptions.map((q) => {
              if (q.id !== question.id) return q;
              const answer = {
                ...q.answer,
                answer: {
                  ...q.answer.answer,
                  [q.id]: value,
                },
              };
              const updatedQuestion = { ...q, answer };
              return updatedQuestion;
            });
            setExemptions(questions);
          }}
          ref={exemptionsForm}
        />
      </Flex>
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderWorksheet()}
          {isExemptFromWithholding && renderExemptions()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (basicInfoForm && basicInfoForm.current) {
              basicInfoForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetAForm && worksheetAForm.current) {
              worksheetAForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetBForm && worksheetBForm.current) {
              worksheetBForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (worksheetForm && worksheetForm.current) {
              worksheetForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (exemptionsForm && exemptionsForm.current) {
              exemptionsForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {

            } else {
              // No error, proceed to save
              const basicInfoData = basicInfo.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

              let worksheetAData;
              let worksheetBData;
              let additionalWithholdingData;
              let stateData;
              let exemptionQuestionsData;
              let domicileData;
              let applicableAllowancesData;

              let secALineITemp = '';
              let secBLineNTemp = '';
              let totalNumAllowancesTemp = '';

              if (isExemptFromWithholding) {
                worksheetAData = resetInitialQuestions(worksheetSectionAQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                applicableAllowancesData = resetInitialQuestions(applicableAllowancesQuestion).reduce(
                  (acc, question) => ({ ...acc, ...question.answer.answer }),
                  {},
                );
                worksheetBData = resetInitialQuestions(worksheetSectionBQuestions)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                additionalWithholdingData = resetInitialQuestions(additionalWithholdingAmtQuestion)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                stateData = stateOfDomicile
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                exemptionQuestionsData = exemptions
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                domicileData = { domicile_not_dc: domicileNotDC };
              } else {
                if (worksheetOpen) {
                  worksheetAData = worksheetA
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  applicableAllowancesData = applicableAllowances
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  worksheetBData = worksheetB
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  additionalWithholdingData = additionalWithholdingAmt
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                } else {
                  // Reset stale dependents value (Line e)
                  const resetWorksheetAQuestions = resetInitialQuestions(worksheetSectionAQuestions);
                  worksheetAData = resetWorksheetAQuestions
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  setWorksheetA(resetWorksheetAQuestions);

                  // Reset stale checkbox values (Line a - h)
                  const resetApplicableAllowanceQuestion = resetInitialQuestions(applicableAllowancesQuestion);
                  applicableAllowancesData = resetApplicableAllowanceQuestion
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  setApplicableAllowances(resetApplicableAllowanceQuestion);

                  // Reset stale itemized values and filing status (Line j & k)
                  const resetWorksheetBQuestions = resetInitialQuestions(worksheetSectionBQuestions);
                  worksheetBData = resetWorksheetBQuestions
                    .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                  setWorksheetB(resetWorksheetBQuestions);
                }

                stateData = resetInitialQuestions(stateOfDomicileQuestions)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
                domicileData = { domicile_not_dc: '' };
                exemptionQuestionsData = resetInitialQuestions(exemptionQuestions)
                  .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

                secALineITemp = calculateSectionAWithholding();
                secBLineNTemp = calculateSectionBWithholding();
                totalNumAllowancesTemp = calculateTotalNumAllowances();
              }
              const calculationsData = {
                sec_a_line_i: secALineITemp.toString(),
                sec_b_line_n: secBLineNTemp.toString(),
                total_num_allowances: totalNumAllowancesTemp.toString(),
              };
              const exemptionData = { is_exempt: isExemptFromWithholding };
              const worksheetData = {
                ...worksheetAData,
                ...worksheetBData,
                ...additionalWithholdingData,
                ...applicableAllowancesData,
              };
              const allExemptionsData = {
                ...stateData,
                ...exemptionQuestionsData,
                ...exemptionData,
                ...domicileData,
              };
              const data = {
                ...basicInfoData,
                ...worksheetData,
                ...allExemptionsData,
                ...calculationsData,
              };
              saveFormData(data, () => {
                setCurrentStep(STEPS.step2);
                scrollTop();
              });
            }
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderWorksheet(true)}
        {isExemptFromWithholding && renderExemptions(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );
  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default DCForm;
